import type { PriceImprovementCreateRequest } from 'apiConnector/price-improvement';
import { priceImprovementApi } from 'apiConnector/price-improvement';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  priceImprovementCancelFailed,
  priceImprovementCancelReceived,
  priceImprovementCreateFailed,
  priceImprovementCreateReceived,
} from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import { getPriceImprovementExpirationTime } from 'state/quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.selectors';
import type { EpicWithThunk } from 'state/state.common';
import { toApiError } from 'utils/http/apiErrorHelper';

export function priceImprovementCreateEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      httpClient,
      selectors: {
        priceImprovementRequest: { getPriceImprovementComment, getPriceImprovementRequestedTradingOffer },
        getQuoteResultFromQuoteId,
      },
    },
  ) =>
    action$.pipe(
      ofType('PRICE_IMPROVEMENT_CREATE_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ quoteId }, state]) => {
        const request: PriceImprovementCreateRequest = {
          quoteId,
          externalQuoteId: getQuoteResultFromQuoteId(state, quoteId)?.quoteExternalId,
          comment: getPriceImprovementComment(state, quoteId),
          requestedTradingOffer: getPriceImprovementRequestedTradingOffer(state, quoteId),
          expirationTime: getPriceImprovementExpirationTime(state, quoteId),
        };

        return priceImprovementApi(httpClient)
          .create(request)
          .pipe(
            map(({ requestForPriceImprovementId }) =>
              priceImprovementCreateReceived(quoteId, requestForPriceImprovementId),
            ),
            catchError(error => {
              return of(priceImprovementCreateFailed(quoteId, toApiError(error)));
            }),
          );
      }),
    );
}

export function priceImprovementCancelEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      httpClient,
      selectors: {
        priceImprovementRequest: { getPriceImprovementId },
      },
    },
  ) =>
    action$.pipe(
      ofType('PRICE_IMPROVEMENT_CANCEL_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ quoteId }, state]) => {
        const priceImprovementId = getPriceImprovementId(state, quoteId);

        return priceImprovementApi(httpClient)
          .cancel(priceImprovementId!)
          .pipe(
            map(() => priceImprovementCancelReceived(quoteId)),
            catchError(error => {
              return of(priceImprovementCancelFailed(quoteId, toApiError(error)));
            }),
          );
      }),
    );
}
